/**
 * The slate-api wire contract, as the browser sees it.
 *
 * These mirror the Prisma enums and route return shapes in the sibling repo
 * (slate-api/prisma/schema.prisma, slate-api/src/routes/*.ts). Dates arrive as
 * ISO strings (JSON has no Date), so they are typed as `string` here and
 * parsed at the edge where they are rendered.
 */

export type Role = 'user' | 'admin' | 'owner';
export type MembershipStatus = 'active' | 'suspended';
/** `incomplete`: the team is named but checkout never finished, so there is no plan yet. */
export type SubscriptionStatus = 'incomplete' | 'trialing' | 'active' | 'past_due' | 'canceled';
export type VideoStatus = 'uploading' | 'processing' | 'ready' | 'failed';
/**
 * What was captured. The API derives this from the upload's content type (a
 * client never sends it), and it decides whether there is a picture at all: an
 * `audio` row has no poster and no HLS ladder, only a progressive AAC/MP4.
 */
export type VideoKind = 'screen' | 'audio';
/**
 * Who may watch, narrowing step by step. Mirrors the Visibility enum in
 * slate-api; every rule that reads it lives there, in policy/video-access.ts.
 *
 * `public`    anyone holding the link.
 * `signedIn`  anyone holding the link who has a Slate account.
 * `team`      the team it was recorded in. Nobody, if there is no team.
 * `invited`   the addresses on the recording's share list, and no one else.
 * `private`   its owner alone.
 */
export type Visibility = 'public' | 'signedIn' | 'team' | 'invited' | 'private';
export type TranscriptStatus = 'pending' | 'ready' | 'failed';

/** Error envelope: every 4xx/5xx from slate-api looks like this. */
export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    details?: unknown;
  };
}

// ---------------------------------------------------------------------------
// Videos
// ---------------------------------------------------------------------------

/** A row in the library grid: GET /v1/videos. */
/**
 * A word to sort recordings by. A team's tag is shared by everyone on the
 * team; a personal one is seen by nobody but its owner.
 */
export interface Tag {
  id: string;
  name: string;
  scope: 'team' | 'personal';
}

export interface VideoSummary {
  id: string;
  shareId: string;
  /** Past the free plan's window; the card shows a lock and the page a wall. */
  locked?: boolean;
  title: string;
  status: VideoStatus;
  kind: VideoKind;
  durationSec: number | null;
  posterKey: string | null;
  /** Served from the CDN; null until the recorder's first frame lands. */
  posterUrl?: string | null;
  visibility: Visibility;
  orgId: string | null;
  ownerId: string | null;
  createdAt: string;
  readyAt: string | null;
  /** Set while in the archive; the sweep removes it thirty days after. */
  deletedAt?: string | null;
  owner?: { name: string | null; avatarUrl: string | null } | null;
  /** The tags on it that the person asking may see. */
  tags?: Tag[];
  _count: { views: number; comments: number; reactions?: number };
}

export interface VideoListResponse {
  videos: VideoSummary[];
  nextCursor: string | null;
  /** Everything matching, not just this page. */
  total?: number;
  /** A personal library on the free plan: the window, and how many sit past it. */
  /** `freeSeatOn`: the team this person is on without a paid seat, when that is why the window applies. */
  freeTier?: { limit: number; locked: number; freeSeatOn?: string | null } | null;
}

/** GET /v1/videos/:id. The full row, plus relations. */
export interface VideoDetail extends Omit<VideoSummary, '_count'> {
  requestLinkId: string | null;
  width: number | null;
  height: number | null;
  submitterName: string | null;
  hlsPrefix: string | null;
  mp4Key: string | null;
  updatedAt: string;
  deletedAt: string | null;
  transcript: { status: TranscriptStatus; vttKey: string | null } | null;
  _count: { views: number; comments: number };
}

export interface UploadPart {
  partNumber: number;
  url: string;
}

/** POST /v1/videos */
export interface CreateVideoResponse {
  videoId: string;
  shareId: string;
  uploadId: string;
  parts: UploadPart[];
}

export interface CreateVideoBody {
  contentType: string;
  title?: string;
  partCount?: number;
  /** A file brought in rather than a take recorded here: a paid seat feature. */
  source?: 'upload';
}

/** POST /v1/videos/:id/parts */
export interface MorePartsResponse {
  parts: UploadPart[];
}

export interface CompletedPart {
  partNumber: number;
  etag: string;
}

/** POST /v1/videos/:id/complete */
export interface CompleteVideoResponse {
  id: string;
  shareId: string;
  status: VideoStatus;
  title: string;
}

// ---------------------------------------------------------------------------
// Share / playback
// ---------------------------------------------------------------------------

export interface Playback {
  /** Null for an audio note: there is no ladder to build from one track. */
  hlsUrl: string | null;
  mp4Url: string | null;
  /** Null for an audio note: there is no frame to grab. */
  posterUrl: string | null;
  captionsUrl: string | null;
}

export type EmailGate = 'none' | 'ask' | 'require';

export interface VideoSettings {
  /** Who may watch. The share list, for `invited`, is its own call. */
  visibility: Visibility;
  captionsOn: boolean;
  playbackRate: number;
  emailGate: EmailGate;
  commentsEnabled: boolean;
  reactionsEnabled: boolean;
  downloadEnabled: boolean;
  analyticsPublic: boolean;
  transcriptPublic: boolean;
  /** Viewers see the links only once the recording has played through. */
  linksAtEnd: boolean;
}

/** A page shown during the recording, at the moment it first came up. */
export interface ShownLink {
  url: string;
  host: string;
  title: string | null;
  /** Seconds on the recording. */
  t: number;
  favicon: string;
}

/** GET /v1/share/:shareId. `playback` is null until status === 'ready'. */
export interface SharePayload {
  shareId: string;
  title: string;
  /** 'default' is the date it was made; a transcript replaces that with 'ai', a person with 'user'. */
  titleSource: 'default' | 'ai' | 'user';
  /**
   * A few sentences on what it covers, written from the transcript or by
   * the owner. Null until there is one, and null for viewers when the
   * transcript is private.
   */
  summary: string | null;
  status: VideoStatus;
  kind: VideoKind;
  durationSec: number | null;
  width: number | null;
  height: number | null;
  createdAt: string;
  readyAt: string | null;
  author: { name: string | null; avatarUrl: string | null } | null;
  playback: Playback | null;
  /**
   * The original upload, for the owner and their team, while `playback` is
   * still null. Short lived; the page swaps to `playback` when it arrives.
   */
  rawPlaybackUrl: string | null;
  /** The recorder's first frame, then the transcoder's poster. Null for audio. */
  posterUrl: string | null;
  /** The library id, present for the owner and their team only. */
  videoId?: string;
  /** True for the owner and their team: people who see it in a library. */
  inLibrary: boolean;
  /**
   * Whose page this is. Present on every payload, the email gate and the plan
   * wall included: those are the team's pages too.
   */
  branding: ShareBranding | null;
  /** An edit is rendering; what plays is the previous version. Owner only. */
  rendering?: boolean;
  /**
   * 'require': nothing to play until an email is given. 'ask': play, but
   * ask once. Null: not asked, or already answered.
   */
  gate: 'ask' | 'require' | 'signIn' | null;
  /**
   * 'signIn': who this recording wants. 'account' is "anyone signed in to
   * Slate", so signing in is the whole of it; 'list' is "only people the
   * owner named", where the address signed in with is what decides. `email`
   * is who the viewer already is, when they are somebody.
   */
  signIn?: { reason: 'account' | 'list'; email: string | null };
  /**
   * Past the free plan's rolling window: nothing plays, for anyone. `owner`
   * says whether the person asking is the one who can fix it.
   */
  locked?: { limit: number; owner: boolean };
  /** What the owner lets a viewer do. Absent on a gated payload. */
  viewer?: {
    captionsOn: boolean;
    playbackRate: number;
    commentsEnabled: boolean;
    reactionsEnabled: boolean;
    downloadUrl: string | null;
    viewCount: number | null;
    linksAtEnd: boolean;
  };
  /** The pages shown during the take, each once. Empty when nothing was noted. */
  links?: ShownLink[];
  /** The owner's controls. Null for everyone else. */
  settings: VideoSettings | null;
  /** Whether this recording's plan includes captions and a transcript. */
  transcriptIncluded: boolean;
  /** Whether the person looking holds a paid seat, which is what the editor needs. */
  editingIncluded?: boolean;
  /** Null when not included, or for videos that predate transcription. */
  transcript: { status: 'pending' | 'ready' | 'failed'; language: string; captionsUrl: string | null } | null;
}

// ---------------------------------------------------------------------------
// Custom branding
// ---------------------------------------------------------------------------

/** One of the team's own recordings, played before or after every other. */
export interface BrandSlate {
  shareId: string;
  title: string;
  /** Progressive MP4: a slate is short, and there is no ladder to negotiate. */
  mp4Url: string;
  durationSec: number | null;
  posterUrl: string | null;
}

/**
 * The team's face on a share page. Null for a recording outside any team, for
 * a team that has set nothing, and for one whose plan is not live: branding
 * comes with the plan, and the settings survive it lapsing.
 */
export interface ShareBranding {
  name: string;
  squareLogoUrl: string | null;
  wideLogoUrl: string | null;
  /** `#rrggbb`. */
  brandColor: string | null;
  /** Black or white, whichever reads on `brandColor`. Computed by the API. */
  brandInk: string | null;
  /** How the page is lit. 'dark' is Slate's own, and the default. */
  theme: BrandTheme;
  /** The team has paid to have the credit line and signup link taken off. */
  hideSlateBranding: boolean;
  intro: BrandSlate | null;
  outro: BrandSlate | null;
  /** Their own hostname, when links are actually being served from it. */
  domain: string | null;
}

export type BrandTheme = 'dark' | 'light';

export type DomainStatus = 'none' | 'verifying' | 'provisioning' | 'active' | 'failed';

/**
 * The finer reading of the walk, and the one the page switches on. `status`
 * records what has been achieved; this says what is happening and whose turn
 * it is. `requesting` is the one worth having: for the seconds between asking
 * AWS for a certificate and AWS naming the record it wants, there is nothing
 * for the team to add yet, and saying "waiting for your DNS record" then sends
 * them looking for a record that does not exist.
 */
export type DomainStage = 'none' | 'requesting' | 'dns' | 'building' | 'live' | 'failed';

export interface DnsRecord {
  type: 'CNAME';
  name: string;
  value: string;
}

/** Where a team's own domain has got to, and what they have to do next. */
export interface DomainState {
  host: string | null;
  status: DomainStatus;
  stage: DomainStage;
  /**
   * Both records the walk asks for, reported from the start so the second is
   * never a surprise. Only the values arrive late: `point` names the CDN,
   * which does not exist until the certificate is issued.
   *
   * `validate` stays after it has done its job, because it has to stay in DNS
   * too: it is checked again when the certificate renews.
   */
  validate: DnsRecord | null;
  point: DnsRecord | null;
  error: string | null;
  checkedAt: string | null;
  verifiedAt: string | null;
  /** False where the deployment cannot provision one; the field is not offered. */
  available: boolean;
}

/** A slate as the settings page lists it. */
export interface BrandingSlateRef {
  id: string;
  shareId: string;
  title: string;
  durationSec: number | null;
  posterUrl: string | null;
  /** False while it processes or once archived: it will not be played. */
  playable: boolean;
}

/** GET /v1/orgs/:id/branding */
export interface BrandingSettings {
  squareLogoUrl: string | null;
  wideLogoUrl: string | null;
  brandColor: string | null;
  brandInk: string | null;
  theme: BrandTheme;
  hideSlateBranding: boolean;
  intro: BrandingSlateRef | null;
  outro: BrandingSlateRef | null;
  /** Whether any of it is being shown to anybody: the plan has to be live. */
  included: boolean;
  /** Admins and owners. A member sees the settings and cannot change them. */
  mayManage: boolean;
  domain: DomainState;
}

export interface Comment {
  id: string;
  authorName: string | null;
  bodyText: string;
  timecodeSec: number | null;
  createdAt: string;
  resolvedAt?: string | null;
  authorUser?: { id: string; name: string | null; avatarUrl: string | null } | null;
}

export interface CommentsResponse {
  comments: Comment[];
}

// ---------------------------------------------------------------------------
// Reactions
// ---------------------------------------------------------------------------

/**
 * A fixed vocabulary, mirroring the `ReactionKind` enum in the API. The wire
 * value is a key, never the emoji: the glyph is a rendering choice that can be
 * swapped without a migration, and a public endpoint that accepted arbitrary
 * strings would be a liability.
 */
export const REACTION_KINDS = ['like', 'love', 'laugh', 'wow', 'clap', 'think'] as const;

export type ReactionKind = (typeof REACTION_KINDS)[number];

/** One reaction, pinned to the second of the recording it belongs to. */
export interface Reaction {
  id: string;
  kind: ReactionKind;
  timecodeSec: number;
  createdAt: string;
}

export type ReactionTotals = Record<ReactionKind, number>;

/**
 * GET /v1/share/:shareId/reactions. Ordered by `timecodeSec`, capped at 1000
 * rows, with the totals counted server-side so a truncated list still reports
 * an honest number.
 */
export interface ReactionsResponse {
  reactions: Reaction[];
  totals: ReactionTotals;
}

/** POST /v1/share/:shareId/reactions. `anonId` stands in for a signed-in id. */
export interface CreateReactionBody {
  kind: ReactionKind;
  timecodeSec: number;
  anonId?: string;
}

// ---------------------------------------------------------------------------
// Organizations
// ---------------------------------------------------------------------------

/** GET /v1/orgs: one entry per active membership, with the caller's role. */
/**
 * A team's seats, split by who pays for them. Seats we have given the team
 * sit on top of the ones it buys, so a team wanting one more buys one more
 * and keeps what it was given. `comped` is 0 for almost everybody, which is
 * how the plan page knows to say nothing about it at all.
 */
export interface SeatBreakdown {
  paid: number;
  comped: number;
  total: number;
}

export interface OrgSummary {
  id: string;
  name: string;
  slug: string;
  subscriptionStatus: SubscriptionStatus;
  trialEndsAt: string | null;
  /** Bought, given, and the two added up. Absent on older API builds. */
  seats?: SeatBreakdown;
  /** Monthly or yearly seats. */
  billingInterval?: BillingInterval;
  /** Set when the owner is winding the plan down; it stops on this date. */
  cancelAt?: string | null;
  /** End of the current billing period. */
  renewsAt?: string | null;
  /** Paid seats the team pays for. */
  seatCount: number;
  /** Paid members plus paid invitations still out. */
  seatsUsed?: number;
  role: Role;
  /** Stripe holds a subscription for it. Without one the trial just runs out. */
  billingSetUp?: boolean;
  /**
   * The team's own hostname for share links, when one is serving and the plan
   * that pays for it is live (custom branding). Null otherwise, and links are
   * written on the Slate address, which never stops working.
   */
  linkDomain?: string | null;
}

export type BillingInterval = 'month' | 'year';

/** What a seat costs, for the plan page. */
export interface Pricing {
  seatMonthlyCents: number;
  /** Null when this deployment offers monthly only. */
  seatYearlyCents: number | null;
  /** Free days at the start of a plan on `trialInterval`; the other interval has none. */
  trialDays: number;
  trialInterval?: 'month' | 'year';
  /** False on deployments without Stripe keys: teams work on their trial, no checkout. */
  enabled: boolean;
}

/** A paid seat carries the plan; a free one is on the team without it. */
export type SeatLevel = 'free' | 'paid';

export interface OrgMember {
  role: Role;
  seat: SeatLevel;
  createdAt: string;
  user: { id: string; email: string; name: string | null; avatarUrl: string | null };
}

export interface OrgInvite {
  id: string;
  email: string;
  name?: string | null;
  role: Role;
  seat: SeatLevel;
  expiresAt: string;
  createdAt: string;
  /**
   * The link to hand the invitee. Null for members who cannot invite, since the
   * token in it is a bearer credential for a seat.
   */
  acceptUrl: string | null;
}

/** GET /v1/orgs/:id */
export interface OrgDetail {
  id: string;
  name: string;
  slug: string;
  subscriptionStatus: SubscriptionStatus;
  trialEndsAt: string | null;
  seatCount: number;
  seatsUsed?: number;
  /** Bought, given, and the two added up. Absent on older API builds. */
  seats?: SeatBreakdown;
  memberships: OrgMember[];
  invites: OrgInvite[];
}

/** POST /v1/orgs/:id/invites */
export interface CreateInviteResponse {
  invite: {
    id: string;
    email: string;
    role: Role;
    expiresAt: string;
    acceptUrl: string;
  };
}

/** POST /v1/orgs: checkoutUrl is null when Stripe is not configured. */
export interface CreateOrgResponse {
  /** The people invited along with the team, with the links that let them in. */
  invites?: { email: string; acceptUrl: string }[];
  org: {
    id: string;
    name: string;
    slug: string;
    subscriptionStatus: SubscriptionStatus;
    trialEndsAt: string | null;
  };
  checkoutUrl: string | null;
}

/** GET /v1/invites/:token: what accepting would do, before it is done. */
export interface InviteDetails {
  org: { id: string; name: string };
  /** Who sent it, for the greeting. */
  invitedBy: string | null;
  /** The address it was sent to. */
  email: string;
  role: Role;
  seat: SeatLevel;
  expiresAt: string;
  /**
   * The team's own look, so the page somebody is deciding on looks like the
   * thing they are deciding about. Null when the team has none, or their plan
   * does not include it.
   */
  branding: ShareBranding | null;
  /** Whether the signed-in account may accept it, and if not, why. */
  canAccept: boolean;
  blocker:
    | { code: 'wrong_account' }
    | { code: 'already_in_org'; orgName: string }
    | { code: 'has_subscription'; orgName: string }
    | null;
}

/** POST /v1/invites/:token/accept */
export interface AcceptInviteResponse {
  org: { id: string; name: string; slug: string };
  role: Role;
}

export interface BillingPortalResponse {
  url: string;
}

// ---------------------------------------------------------------------------
// Request links
// ---------------------------------------------------------------------------

export interface RequestLink {
  id: string;
  token: string;
  label: string;
  disabled: boolean;
  expiresAt: string | null;
  maxVideos: number | null;
  createdAt: string;
  _count: { videos: number };
}

export interface RequestLinksResponse {
  links: RequestLink[];
}

/** POST /v1/request-links returns the row without `disabled`/`_count`. */
export interface CreatedRequestLink {
  id: string;
  token: string;
  label: string;
  expiresAt: string | null;
  maxVideos: number | null;
  createdAt: string;
}

/** GET /v1/r/:token. Public, deliberately says nothing about the requester. */
export interface RequestLinkPublic {
  label: string;
  requestedBy: string;
  maxUploadBytes: number;
}
